/**
 * useServiceTypes - Tipos de Serviço da Ordem de Serviço (Fase 3)
 *
 * Leitura e gravação 100% via RPC (get_service_types / upsert_service_type /
 * delete_service_type). O cliente `supabase` é preservado com `.call` para não
 * perder o `this` em RPCs fora da tipagem gerada.
 *
 * RELEASE: 2026-10-08 v1.0.0 - Versão inicial.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/** Movimentação de estoque feita na conclusão da OS. */
export type ServiceStockMovement = 'none' | 'install' | 'uninstall' | 'swap';
/** Etapas dos checklists, executadas nesta ordem. */
export type ServiceChecklistStage = 'start' | 'execution' | 'completion';
export type ServiceStockItemType = 'kit' | 'tracker' | 'chip';

export interface ServiceTypeChecklistLink {
  template_id: string;
  template_name?: string;
  stage: ServiceChecklistStage;
  sort_order: number;
}

export interface ServiceType {
  id: string;
  name: string;
  description: string;
  color: string;
  duration_minutes: number;
  sort_order: number;
  requires_vehicle: boolean;
  requires_address: boolean;
  default_category_id: string | null;
  default_category_name: string | null;
  default_phase_id: string | null;
  default_phase_name: string | null;
  stock_movement: ServiceStockMovement;
  stock_item_types: ServiceStockItemType[];
  stock_selection_required: boolean;
  /** BR-OS-020: exige vendedor na OS deste tipo. */
  requires_seller?: boolean;
  is_active: boolean;
  checklist_templates: ServiceTypeChecklistLink[];
}

export type ServiceTypeInput = Omit<ServiceType, 'id' | 'default_category_name' | 'default_phase_name'> & { id?: string | null };

export const STOCK_MOVEMENT_OPTIONS: { value: ServiceStockMovement; label: string; hint: string }[] = [
  { value: 'install', label: 'Entrada (Instalação)', hint: 'Prestador → Cliente' },
  { value: 'uninstall', label: 'Saída (Desinstalação)', hint: 'Cliente → Prestador' },
  { value: 'swap', label: 'Ambos (Troca)', hint: 'Remove o antigo e instala o novo' },
  { value: 'none', label: 'Nenhum', hint: 'Sem movimentação (ex.: revisão)' },
];

export const CHECKLIST_STAGES: { value: ServiceChecklistStage; label: string }[] = [
  { value: 'start', label: 'Início' },
  { value: 'execution', label: 'Execução' },
  { value: 'completion', label: 'Conclusão' },
];

export const STOCK_ITEM_TYPE_OPTIONS: { value: ServiceStockItemType; label: string }[] = [
  { value: 'kit', label: 'Kit' },
  { value: 'tracker', label: 'Rastreador' },
  { value: 'chip', label: 'Chip' },
];

const rpc = (name: string, args: Record<string, unknown>) => (supabase.rpc as any).call(supabase, name, args);

export function useServiceTypes(onlyActive = false) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;
  const qc = useQueryClient();
  const key = ['service-types', tenantId, onlyActive];

  const query = useQuery({
    queryKey: key,
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await rpc('get_service_types', { p_only_active: onlyActive, p_tenant_hint: tenantId });
      if (error) throw error;
      return (data ?? []) as ServiceType[];
    },
  });

  const upsert = useMutation({
    mutationFn: async (input: ServiceTypeInput) => {
      const { data, error } = await rpc('upsert_service_type', {
        p_id: input.id ?? null,
        p_name: input.name,
        p_description: input.description,
        p_color: input.color,
        p_duration_minutes: input.duration_minutes,
        p_sort_order: input.sort_order,
        p_requires_vehicle: input.requires_vehicle,
        p_requires_address: input.requires_address,
        p_default_category_id: input.default_category_id,
        p_default_phase_id: input.default_phase_id,
        p_stock_movement: input.stock_movement,
        p_stock_item_types: input.stock_item_types,
        p_stock_selection_required: input.stock_selection_required,
        p_is_active: input.is_active,
        p_requires_seller: !!input.requires_seller,
        p_checklist_templates: input.checklist_templates.map(({ template_id, stage, sort_order }) => ({ template_id, stage, sort_order })),
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-types'] }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await rpc('delete_service_type', { p_id: id, p_tenant_hint: tenantId });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-types'] }),
  });

  return {
    serviceTypes: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    upsert: upsert.mutateAsync,
    isSaving: upsert.isPending,
    remove: remove.mutateAsync,
  };
}
